import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Form
} from 'reactstrap';
import { useLazyQuery } from '@apollo/client';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../assets/css/override.css';
import '../../../assets/css/customDatePickerWidth.css';
import { ReportButtons } from '../../../components/Form/Button';
import SelectEmpresa from '../../../components/Select/SelectEmpresa';
import SelectProductPoints from '../../../components/Select/SelectProductPoints';
import SelectClients from '../../../components/Select/SelectClients';
import DataTable from '../../../components/Table';
import { toast } from 'react-toastify';
import { GET_PRODUCT_RESCUE_BY_CLIENT_REPORT } from '../../../store/queries/supply';

const columnHead = [
  {
    title: 'Cliente',
    field: 'clientName',
    width: 180,
    cellStyle: { textAlign: 'left' },
    headerStyle: { textAlign: 'left' }
  },
  {
    title: 'Produto',
    field: 'produtoName',
    width: 150,
    cellStyle: { textAlign: 'left' },
    headerStyle: { textAlign: 'left' }
  },
  {
    title: 'Empresa',
    field: 'empresaName',
    width: 150,
    cellStyle: { textAlign: 'left' },
    headerStyle: { textAlign: 'left' }
  },
  {
    title: 'Quantidade',
    field: 'quantidade',
    width: 150,
    cellStyle: { textAlign: 'center' },
    headerStyle: { textAlign: 'center' }
  },
  {
    title: 'Data do Resgate',
    field: 'dataResgate',
    width: 120,
    cellStyle: { textAlign: 'center' },
    headerStyle: { textAlign: 'center' }
  },
  {
    title: 'Status',
    field: 'status',
    width: 120,
    cellStyle: { textAlign: 'center' },
    headerStyle: { textAlign: 'center' }
  },
  {
    title: 'Data da Retirada',
    field: 'dataRetirada',
    width: 130,
    cellStyle: { textAlign: 'center' },
    headerStyle: { textAlign: 'center' }
  }
];

const formatData = data => {
  return data.map(item => ({
    clientName: item.client?.name || 'Não informado',
    empresaName: item.empresa?.name || 'Não informado',
    empresaType: item.empresa?.type === 'store' ? 'Loja' : 'Posto',
    produtoName: item.produto?.name || 'Não informado',
    quantidade: item.quantidade || 1,
    dataResgate: item.dataResgate
      ? new Date(item.dataResgate).toLocaleDateString('pt-BR')
      : 'Não informado',
    status: item.status || 'Resgatado',
    dataRetirada: item.dataRetirada
      ? new Date(item.dataRetirada).toLocaleDateString('pt-BR')
      : '-'
  }));
};

function ResgateProdutosPorCliente({ user }) {
  const [startDate, setStartDate] = useState(
    dayjs().subtract(90, 'day').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [selectedProdutos, setSelectedProdutos] = useState([]);
  const [selectedEmpresas, setSelectedEmpresas] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('todos');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [getReport] = useLazyQuery(GET_PRODUCT_RESCUE_BY_CLIENT_REPORT, {
    fetchPolicy: 'network-only'
  });

  const handleConfirm = useCallback(async () => {
    if (!startDate || !endDate) {
      toast.error('Por favor, selecione as datas inicial e final');
      return;
    }

    setLoading(true);

    try {
      // Preparar as variáveis para a query
      const gasstations = selectedEmpresas
        .filter(item => item.type === 'gasstation')
        .map(item => item.value);

      const stores = selectedEmpresas
        .filter(item => item.type === 'store')
        .map(item => item.value);

      const product =
        selectedProdutos.length > 0
          ? selectedProdutos.map(item => item.value)
          : null;

      // Validação específica para produtos
      if (selectedProdutos.length > 0) {
        console.log('Produtos selecionados para filtro:', selectedProdutos);
        console.log('IDs dos produtos para filtro:', product);
      } else {
        console.log('Nenhum produto selecionado - filtro não será aplicado');
      }

      const variables = {
        customer: user.customer,
        client: selectedClient?.value || null,
        gasstations: gasstations.length > 0 ? gasstations : null,
        stores: stores.length > 0 ? stores : null,
        product: product,
        dataInicio: new Date(startDate),
        dataFim: new Date(endDate),
        status: selectedStatus
      };

      console.log('Filtros aplicados:', {
        startDate,
        endDate,
        selectedClient: selectedClient?.value,
        selectedEmpresas: selectedEmpresas.map(item => ({
          name: item.label,
          type: item.type,
          value: item.value
        })),
        selectedProdutos: selectedProdutos.map(item => ({
          name: item.label,
          value: item.value
        })),
        variables
      });

      const { data } = await getReport({ variables });
      console.log('Resposta da API:', data);

      if (
        data &&
        data.productRescueByClientReport &&
        data.productRescueByClientReport.length > 0
      ) {
        const formattedData = formatData(data.productRescueByClientReport);
        setRows(formattedData);
        toast.success(`Relatório gerado com ${formattedData.length} registros`);
      } else {
        setRows([]);
        toast.info('Nenhum registro encontrado para os filtros selecionados');
      }
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    selectedClient,
    selectedEmpresas,
    selectedProdutos,
    selectedStatus,
    user.customer,
    getReport
  ]);

  const handleClear = useCallback(() => {
    setStartDate(dayjs().subtract(90, 'day').format('YYYY-MM-DD'));
    setEndDate(dayjs().format('YYYY-MM-DD'));
    setSelectedProdutos([]);
    setSelectedEmpresas([]);
    setSelectedClient(null);
    setSelectedStatus('todos');
    setRows([]);
  }, []);

  return (
    <div className=''>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <Form>
                <Row>
                  <Col lg={3}>
                    <FormGroup>
                      <Label for='dataInicio'>Data Inicial *</Label>
                      <Input
                        value={startDate}
                        type='date'
                        name='dataInicio'
                        onChange={e => setStartDate(e.target.value)}
                        id='dataInicio'
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={3}>
                    <FormGroup>
                      <Label for='dataFim'>Data Final *</Label>
                      <Input
                        value={endDate}
                        type='date'
                        name='dataFim'
                        onChange={e => setEndDate(e.target.value)}
                        id='dataFim'
                      />
                    </FormGroup>
                  </Col>
                  <Col lg={3}>
                    <SelectClients
                      label='Cliente'
                      ismulti={false}
                      user={user}
                      parentCallback={setSelectedClient}
                      value={selectedClient}
                      placeholder='Selecione o cliente...'
                    />
                  </Col>
                  <Col lg={3}>
                    <SelectEmpresa
                      label='Empresas'
                      ismulti={true}
                      user={user}
                      parentCallback={setSelectedEmpresas}
                      value={selectedEmpresas}
                      placeholder='Selecione a(s) empresa(s)...'
                    />
                  </Col>
                </Row>

                <Row>
                  <Col lg={3}>
                    <SelectProductPoints
                      label='Produto'
                      ismulti={true}
                      user={user}
                      parentCallback={setSelectedProdutos}
                      value={selectedProdutos}
                      placeholder='Selecione o(s) produto(s)...'
                    />
                  </Col>
                  <Col lg={3}>
                    <FormGroup>
                      <Label for='status'>Status</Label>
                      <Input
                        type='select'
                        name='status'
                        id='status'
                        value={selectedStatus}
                        onChange={e => setSelectedStatus(e.target.value)}
                      >
                        <option value='todos'>Todos</option>
                        <option value='resgatado'>Resgatado</option>
                        <option value='retirado'>Retirado</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col lg={8}>
                    <ReportButtons
                      onCancel={handleClear}
                      onConfirm={handleConfirm}
                    />
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Card>
        <CardBody>
          <DataTable
            rows={rows}
            rowHead={columnHead}
            title={'Relatório de Produtos Resgatados por Cliente'}
            loading={loading}
            searchInput
            showPagination={true}
            showTitle={false}
            showShadow={false}
            showExportButton={true}
          />
        </CardBody>
      </Card>
    </div>
  );
}

const mapStateToProps = state => ({
  user: state.user
});

export default connect(mapStateToProps)(ResgateProdutosPorCliente);
